/**
 * 使用者管理 (/users, boards/Users.dc.html) — super_admin. Pending Access sign-ins (approve
 * needs a role; default 管理員) above the account table (search, inline role change, 撤銷權限 /
 * 恢復). The v1 admin-audit tab moved to /audit. The last-super_admin guard is server-side; its
 * error shows inline. Your own row cannot be revoked or demoted here (self-lockout).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { errMsg } from '@/components/Dialogs';
import { useAuth } from '@/contexts/AuthContext';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { fmtAgo, fmtDate } from '@/lib/format';
import { ROLE_LABEL_ZH } from '@/lib/labels';
import type { AdminStatus, AdminUser, Role } from '@/types';

const ROLES: Role[] = ['admin', 'support', 'read_only', 'super_admin'];
const STATUS_LABEL: Record<AdminStatus, string> = { pending: '待審核', approved: '已核准', revoked: '已撤銷' };

export function Users() {
  const { email: me } = useAuth();
  const roster = usePoll<AdminUser[]>(() => api.listAdminUsers(), 15_000);
  const all = roster.data ?? [];
  const pending = all.filter((u) => u.status === 'pending');
  const accounts = all.filter((u) => u.status !== 'pending');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});

  const act = async (email: string, fn: () => Promise<unknown>) => {
    setBusy(email);
    setErrs(({ [email]: _drop, ...rest }) => rest);
    try { await fn(); roster.refresh(); }
    catch (e) { setErrs((m) => ({ ...m, [email]: errMsg(e) })); }
    finally { setBusy(null); }
  };

  const needle = q.trim().toLowerCase();
  const rows = useMemo(
    () =>
      accounts
        .filter((u) => !needle || u.email.toLowerCase().includes(needle) || (u.display_name ?? '').toLowerCase().includes(needle))
        .sort((a, b) => Number(a.status === 'revoked') - Number(b.status === 'revoked') || a.email.localeCompare(b.email)),
    [accounts, needle],
  );

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <h1 className="v2-title">使用者管理</h1>
          <p className="v2-sub">後台帳號與角色 · 經 Cloudflare Access 登入的人會先進待審核 · 帳號的變更紀錄在 <Link className="link" to="/audit?category=account">稽核日誌</Link></p>
        </div>
      </div>

      {Boolean(roster.error) && !roster.data && <div className="error-line">載入失敗:{errMsg(roster.error)}</div>}

      {pending.length > 0 && (
        <section className="panel flush pending-card">
          <div className="bar">
            <h2>待審核 {pending.length}</h2>
            <span className="muted-note">核准時必須指定角色</span>
          </div>
          {pending.map((u) => <PendingRow key={u.email} u={u} busy={busy === u.email} err={errs[u.email]} act={act} />)}
        </section>
      )}

      <section className="panel flush">
        <div className="tpanel-bar">
          <strong style={{ fontSize: 'var(--text-small)' }}>後台帳號 {accounts.length}</strong>
          <span className="grow" />
          <label className="searchbox" style={{ width: 240 }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email 或名稱" aria-label="搜尋帳號" />
          </label>
        </div>
        <div className="tscroll">
          <table className="v2-table">
            <thead>
              <tr><th>Email</th><th>名稱</th><th>角色</th><th>狀態</th><th>最後更新</th><th /></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="empty-row"><td colSpan={6}>{roster.loading && !roster.data ? '載入中…' : '沒有符合的帳號'}</td></tr>
              ) : (
                rows.map((u) => {
                  const self = u.email === me;
                  const isBusy = busy === u.email;
                  return (
                    <tr key={u.email}>
                      <td>{u.email}{self && <span className="muted"> (你自己)</span>}</td>
                      <td>{u.display_name || '—'}</td>
                      <td>
                        {u.status === 'approved' && !self ? (
                          <select
                            className="v2-select"
                            aria-label={`${u.email} 的角色`}
                            value={u.role ?? ''}
                            disabled={isBusy}
                            onChange={(e) => void act(u.email, () => api.setAdminRole(u.email, e.target.value as Role, 'approved'))}
                          >
                            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL_ZH[r]}</option>)}
                          </select>
                        ) : (
                          u.role && u.status !== 'revoked' ? ROLE_LABEL_ZH[u.role] : '—'
                        )}
                      </td>
                      <td><span className={u.status === 'approved' ? 'tone-success' : 'tone-muted'}>{STATUS_LABEL[u.status]}</span></td>
                      <td className="muted nowrap" title={fmtDate(u.last_active_at ?? u.updated_at)}>{fmtAgo(u.last_active_at ?? u.updated_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {errs[u.email] && <span className="error-line" role="alert" style={{ marginRight: 8 }}>{errs[u.email]}</span>}
                        {u.status === 'approved' && !self && (
                          <button type="button" className="btn btn-outline-danger" disabled={isBusy} onClick={() => void act(u.email, () => api.setAdminRole(u.email, u.role, 'revoked'))}>
                            撤銷權限
                          </button>
                        )}
                        {u.status === 'revoked' && (
                          <button type="button" className="btn btn-outline" disabled={isBusy} onClick={() => void act(u.email, () => api.setAdminRole(u.email, u.role ?? 'read_only', 'approved'))}>
                            恢復
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="pager">
          <span className="grow">
            最後一位超級管理員不能被降級或撤銷。角色:超級管理員 = 全部 · 管理員 = 名單、機型、序號 · 客服 = 工單 · 唯讀 = 總覽與營運洞察。
          </span>
        </div>
      </section>
    </div>
  );
}

function PendingRow({
  u, busy, err, act,
}: {
  u: AdminUser; busy: boolean; err?: string; act: (email: string, fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [role, setRole] = useState<Role>('admin');
  return (
    <div className="row" style={{ gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-5)', borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div>{u.email}</div>
        <div className="muted-note">{u.display_name || '—'} · 申請 <span title={fmtDate(u.requested_at)}>{fmtAgo(u.requested_at)}</span></div>
      </div>
      <label className="inline-field" style={{ fontSize: 'var(--text-caption)' }}>角色
        <select className="v2-select" value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={busy}>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL_ZH[r]}</option>)}
        </select>
      </label>
      <button type="button" className="btn btn-outline" disabled={busy} onClick={() => void act(u.email, () => api.setAdminRole(u.email, null, 'revoked'))}>拒絕</button>
      <Button variant="primary" loading={busy} onClick={() => void act(u.email, () => api.setAdminRole(u.email, role, 'approved'))}>核准</Button>
      {err && <span className="error-line" role="alert">{err}</span>}
    </div>
  );
}
